import supabase from '../supabase/supabase';

/**
 * Senior SaaS Profile Service
 * Handles user profile data and security updates with Supabase.
 */
class ProfileService {
  async getCurrentUser() {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) throw new Error("Authentication required");
    return user;
  }

  async getProfile() {
    try {
      const user = await this.getCurrentUser();

      await supabase.rpc('ensure_current_user_workspace');

      const { data, error } = await supabase
        .from('profiles_20240320')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return this.createDefaultProfile(user);
        }
        throw error;
      }

      return {
        ...data,
        email: user.email,
        role: data.role || 'Member'
      };
    } catch (err) {
      console.error("Fetch Profile Error:", err);

      try {
        const { data: authData, error: authError } = await supabase.auth.getUser();
        const user = authData?.user;

        if (!authError && user) {
          const fullName = user.user_metadata?.full_name || '';
          return {
            id: user.id,
            email: user.email || '',
            first_name: user.user_metadata?.first_name || fullName.split(' ')[0] || '',
            last_name: user.user_metadata?.last_name || fullName.split(' ').slice(1).join(' ') || '',
            phone: user.user_metadata?.phone || '',
            role: user.user_metadata?.role || 'Member',
            preferences: {
              theme: 'light',
              notifications: true
            }
          };
        }
      } catch (fallbackError) {
        console.error("Profile fallback Error:", fallbackError);
      }

      throw err;
    }
  }

  async createDefaultProfile(user) {
    const { data, error } = await supabase
      .from('profiles_20240320')
      .insert([
        {
          id: user.id,
          first_name: user.user_metadata?.first_name || user.user_metadata?.full_name?.split(' ')[0] || '',
          last_name: user.user_metadata?.last_name || user.user_metadata?.full_name?.split(' ').slice(1).join(' ') || '',
          role: 'Member',
          preferences: { theme: 'light', notifications: true }
        }
      ])
      .select()
      .single();

    if (error) throw error;

    return {
      ...data,
      email: user.email,
      role: data.role || 'Member'
    };
  }

  async updateProfile(payload) {
    try {
      const user = await this.getCurrentUser();
      const firstName = payload.first_name || '';
      const lastName = payload.last_name || '';

      const { data, error } = await supabase
        .from('profiles_20240320')
        .update({
          first_name: firstName,
          last_name: lastName,
          phone: payload.phone,
          job_title: payload.job_title,
          bio: payload.bio,
          avatar_url: payload.avatar_url,
          preferences: payload.preferences,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id)
        .select()
        .single();

      if (error) throw error;

      await supabase.auth.updateUser({
        data: {
          ...user.user_metadata,
          first_name: firstName,
          last_name: lastName,
          full_name: `${firstName} ${lastName}`.trim()
        }
      });

      return data;
    } catch (err) {
      console.error("Update Profile Error:", err);
      throw err;
    }
  }

  async updatePassword(newPassword) {
    if (typeof newPassword !== 'string' || newPassword.length < 8) {
      throw new Error('Password must be at least 8 characters.');
    }

    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !sessionData.session) {
        throw new Error('Your session has expired. Please sign in again before updating your password.');
      }

      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) throw updateError;

      const { data: refreshedUserData, error: refreshError } = await supabase.auth.getUser();

      if (refreshError || !refreshedUserData.user) {
        throw new Error('Password was updated, but the updated authentication state could not be verified.');
      }

      return refreshedUserData.user;
    } catch (err) {
      console.error("Update Password Error:", err);
      throw err;
    }
  }
}

export const profileService = new ProfileService();